import { useState, useEffect } from "react";
import ReportRevenueBoard from "./ReportRevenueBoard";
import { Input, DatePicker, Pagination, Select, message, ConfigProvider } from "antd";
import { SALES_REPORT_PAYMENT_TYPE, REPORT_ORDER_TYPE, PAGINATION_PAGE_OPTIONS } from "../../../constant/constants";
import ReportTable from "./ReportTable";
import { ReportService } from "../../../services/reports-services";
import dayjs from "dayjs";
import { isDate1GreaterThanDate2 } from "../../../utils/Common";
import useUserStore from "../../../stores/store";
import { useNavigate } from "react-router-dom";

export default function OrderReportComponent({
  summaryData,
  reportData,
  setOrdersFilter,
  handleFilterApply,
  page,
  perPage,
  setPerPage,
  total,
  handlePageChange,
  setIsFilterClear,
  handleDownload,
  loading,
}) {
  const { showNotification } = useUserStore();
  const [orderNo, setOrderNo] = useState("");
  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [orderType, setOrderType] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setOrdersFilter((prev) => ({
      ...prev,
      orderNo: orderNo.trim(),
      fromDateTime,
      toDateTime,
      orderType,
      paymentType,
      page,
      perPage,
    }));
  }, [orderNo, fromDateTime, toDateTime, orderType, paymentType, page, perPage]);

  function onHandleOrderNo(e) {
    const val = e.target.value;
    if (ReportService.isTextValid(val)) {
      return;
    }
    setOrderNo(val);
  }

  function onHandleFromDate(date, dateString) {
    if (isDate1GreaterThanDate2(dateString, toDateTime)) {
      showNotification("From Date time cannot be greater than To Date time.");
      return;
    }

    setFromDateTime(dateString);
  }

  function onHandleToDate(date, dateString) {
    if (date) {
      const selectedTime = date.format("HH:mm:ss");
      const timeToSet = selectedTime === "00:00:00" ? "23:59:59" : selectedTime;
      const tempToDate = date.format(`YYYY-MM-DD ${timeToSet}`);
      if (fromDateTime != "" && !(new Date(tempToDate) >= new Date(fromDateTime))) {
        message.warning("This date must be grater than from date.");
        setToDateTime("");
        return;
      }
      setToDateTime(tempToDate);
    } else {
      setToDateTime("");
    }
  }

  function onHandleOrderType(value) {
    setOrderType(value);
  }

  function onHandlePaymentType(value) {
    setPaymentType(value);
  }

  function onHandleClearFilter() {
    setIsFilterClear(true);
    setOrderNo("");
    setFromDateTime("");
    setToDateTime("");
    setOrderType("");
    setPaymentType("");

    setOrdersFilter((prev) => ({
      restaurantId: prev.restaurantId,
      page: 1,
      perPage: 10,
    }));
  }

  function onHandleView(reportId) {
    if (reportId == null) {
      showNotification("Something went wrong. Try again later.");
      return;
    };
    let url = `/reports/orders/${reportId}/details`;
    navigate(url);
  }

  return (
    <section>
      {/* revenue board */}
      <ReportRevenueBoard summaryData={summaryData} data={reportData} handleDownload={handleDownload} />

      {/* filters */}
      <ConfigProvider
        theme={{
          components: {
            DatePicker: {
              zIndexPopup: 1,
            },
            Select: {
              zIndexPopup: 1,
            },
          },
        }}
      >
        <div className="flex items-center space-between w-full mt-[20px] mb-[31px]">
          <div className="flex items-center space-x-[15px] items-center w-full">
            <div className="h-[48px] w-[20%]">
              <Input
                className="h-full w-full"
                value={orderNo || ""}
                onChange={onHandleOrderNo}
                placeholder="Order No"
              />
            </div>
            <div className="h-[48px] w-[18%]">
              <DatePicker
                className={"h-full w-full"}
                showTime={{ format: "HH:mm", showSecond: false }}
                format="YYYY-MM-DD HH:mm"
                value={fromDateTime ? dayjs(fromDateTime) : null}
                onChange={onHandleFromDate}
                placeholder="From Date / Time"
                needConfirm
                disabledDate={(current) => current && current > dayjs().endOf("day")}
              />
            </div>
            <div className="h-[48px] w-[18%]">
              <DatePicker
                className={"h-full w-full"}
                showTime={{ format: "HH:mm", showSecond: false }}
                format="YYYY-MM-DD HH:mm"
                value={toDateTime ? dayjs(toDateTime) : null}
                onChange={onHandleToDate}
                placeholder="To Date / Time"
                needConfirm
                disabledDate={(current) => {
                  if (!fromDateTime) {
                    return current && current > dayjs().endOf("day");
                  }
                  return (current && current < dayjs(fromDateTime).startOf("day")) || current > dayjs().endOf("day");
                }}
              />
            </div>
            <div className="h-[48px] w-[15%]">
              <Select
                className="w-full h-full"
                showSearch={false}
                value={orderType || null}
                onChange={onHandleOrderType}
                placeholder="Order Type"
                optionFilterProp="label"
                options={REPORT_ORDER_TYPE}
              />
            </div>
            <div className="h-[48px] w-[15%]">
              <Select
                className="w-full h-full"
                showSearch={false}
                value={paymentType || null}
                onChange={onHandlePaymentType}
                placeholder="Payment"
                optionFilterProp="label"
                options={SALES_REPORT_PAYMENT_TYPE}
              />
            </div>
          </div>
          <div className="flex items-center space-x-[20px] h-[48px] w-[200px]">
            <button
              className="w-full h-full bg-black text-[14px] font-bold leading-[16px] text-white rounded-[5px] w-[50%]"
              onClick={onHandleClearFilter}
            >
              Clear
            </button>
            <button
              className="w-full h-full bg-[#FCBD10] text-[14px] font-bold leading-[16px] text-black rounded-[5px] w-[50%]"
              onClick={handleFilterApply}
            >
              Apply
            </button>
          </div>
        </div>
      </ConfigProvider>

      {/* sales report table */}
      <div>
        <ReportTable 
          data={reportData} 
          context={"orders"} 
          page={page} 
          perPage={perPage} 
          isLoading={loading} 
          actions={['view']} 
          handleView={onHandleView} />
        <div className="mt-[20px] mb-[50px] report-pagination relative custom-pagination-options">
          <Pagination
            showSizeChanger
            onChange={handlePageChange}
            onShowSizeChange={(current, size) => {
              setPerPage(size);
            }}
            current={page}
            pageSize={perPage}
            total={total}
            pageSizeOptions={PAGINATION_PAGE_OPTIONS}
            locale={{ items_per_page: "" }}
            showQuickJumper={false}
          />

          <div
            className="absolute h-[48px] flex items-center space-x-[40px] 
          top-0 right-[122px] text-[18px] font-medium text-[#7D7D7D] mr-3"
          >
            <div>Total: {total}</div>
            <div>Row </div>
          </div>
        </div>
      </div>
    </section>
  );
}
